const LANG_MAP = {
  js: 'js',
  javascript: 'js',
  ts: 'js',
  typescript: 'js',
  json: 'json',
  html: 'html',
  xml: 'html',
  css: 'css',
  py: 'py',
  python: 'py'
};

export function render({ node }) {
  const root = document.createElement('div');
  root.className = 'ew-code-snippet';

  const header = document.createElement('div');
  header.className = 'ew-node-header';
  header.dataset.ewDrag = 'true';
  header.dataset.ewSelect = 'true';
  header.dataset.ewLink = 'true';

  const title = document.createElement('span');
  title.textContent = node.props?.title || 'Code Snippet';

  const anchor = document.createElement('span');
  anchor.className = 'ew-link-anchor';
  anchor.dataset.ewLink = 'true';
  anchor.title = 'Link';

  header.append(title, anchor);

  const body = document.createElement('div');
  body.className = 'ew-node-body';

  const label = document.createElement('label');
  label.textContent = node.props?.label || node.props?.filename || 'Snippet';

  const pre = document.createElement('pre');
  pre.className = 'ew-code-block';
  const codeEl = document.createElement('code');
  pre.appendChild(codeEl);

  body.append(label, pre);
  root.append(header, body);

  const updateSnippet = (next) => {
    title.textContent = next.props?.title || 'Code Snippet';
    label.textContent = next.props?.label || next.props?.filename || 'Snippet';
    const code = next.data?.code ?? next.props?.code ?? '';
    const lang = resolveLanguage(next, code);
    const html = highlight(code, lang);
    codeEl.className = `ew-code ew-code-${lang}`;
    codeEl.innerHTML = html;
  };

  updateSnippet(node);

  return {
    el: root,
    update: updateSnippet
  };
}

function resolveLanguage(node, code) {
  const raw = (node.props?.language || node.data?.language || '').toString().toLowerCase();
  if (raw && LANG_MAP[raw]) return LANG_MAP[raw];
  const filename = (node.props?.filename || '').toLowerCase();
  if (filename.endsWith('.json')) return 'json';
  if (filename.endsWith('.html') || filename.endsWith('.htm')) return 'html';
  if (filename.endsWith('.css')) return 'css';
  if (filename.endsWith('.py')) return 'py';
  if (filename.endsWith('.ts') || filename.endsWith('.js')) return 'js';
  return detectLanguage(code);
}

function detectLanguage(code) {
  const text = code || '';
  if (/^\s*[{[]/.test(text) && /"\w+"\s*:/.test(text)) return 'json';
  if (/<\/[a-z]/i.test(text) || /<[a-z][^>]*>/.test(text)) return 'html';
  if (/\b(def|import|from|class)\b/.test(text)) return 'py';
  if (/[a-z-]+\s*:\s*[^;]+;/.test(text)) return 'css';
  if (/\b(const|let|function|=>|class)\b/.test(text)) return 'js';
  return 'plain';
}

function highlight(code, lang) {
  if (!code) return '';
  if (lang === 'json') return scan(code, jsonRules());
  if (lang === 'html') return scan(code, htmlRules());
  if (lang === 'css') return scan(code, cssRules());
  if (lang === 'py') return scan(code, pyRules());
  if (lang === 'js') return scan(code, jsRules());
  return escapeHtml(code);
}

function scan(code, rules) {
  let out = '';
  let i = 0;
  while (i < code.length) {
    let matched = false;
    for (const rule of rules) {
      rule.regex.lastIndex = i;
      const m = rule.regex.exec(code);
      if (m && m.index === i) {
        const token = m[0];
        const type = typeof rule.type === 'function' ? rule.type(token, code, i) : rule.type;
        out += wrap(token, type);
        i += token.length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      out += escapeChar(code[i]);
      i += 1;
    }
  }
  return out;
}

function jsonRules() {
  return [
    { regex: /"(?:\\.|[^"\\])*"/y, type: (token, code, i) => isJsonKey(code, i + token.length) ? 'tok-key' : 'tok-str' },
    { regex: /\b-?(?:0x[\da-fA-F]+|\d+(?:\.\d+)?)(?:e[+-]?\d+)?\b/y, type: 'tok-num' },
    { regex: /\b(true|false|null)\b/y, type: 'tok-kw' }
  ];
}

function jsRules() {
  return [
    { regex: /\/\/[^\n]*/y, type: 'tok-com' },
    { regex: /\/\*[\s\S]*?\*\//y, type: 'tok-com' },
    { regex: /'(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*"|`(?:\\.|[^`\\])*`/y, type: 'tok-str' },
    { regex: /\b-?(?:0x[\da-fA-F]+|\d+(?:\.\d+)?)(?:e[+-]?\d+)?\b/y, type: 'tok-num' },
    { regex: /\b(const|let|var|function|return|if|else|for|while|switch|case|break|continue|class|new|try|catch|finally|throw|await|async|import|from|export|default|extends|super|this|typeof|instanceof)\b/y, type: 'tok-kw' },
    { regex: /\b(true|false|null|undefined)\b/y, type: 'tok-lit' }
  ];
}

function htmlRules() {
  return [
    { regex: /<!--[\s\S]*?-->/y, type: 'tok-com' },
    { regex: /<\/?[a-zA-Z0-9:-]+/y, type: 'tok-tag' },
    { regex: /\s+[a-zA-Z0-9:-]+(?=\s*=)/y, type: 'tok-attr' },
    { regex: /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/y, type: 'tok-str' }
  ];
}

function cssRules() {
  return [
    { regex: /\/\*[\s\S]*?\*\//y, type: 'tok-com' },
    { regex: /[a-zA-Z-]+(?=\s*:)/y, type: 'tok-key' },
    { regex: /#[\da-fA-F]{3,8}\b/y, type: 'tok-num' },
    { regex: /\b-?(?:\d+(?:\.\d+)?)(px|em|rem|vh|vw|%)?\b/y, type: 'tok-num' },
    { regex: /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/y, type: 'tok-str' }
  ];
}

function pyRules() {
  return [
    { regex: /#[^\n]*/y, type: 'tok-com' },
    { regex: /'''[\s\S]*?'''|"""[\s\S]*?"""|'(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*"/y, type: 'tok-str' },
    { regex: /\b-?(?:\d+(?:\.\d+)?)(?:e[+-]?\d+)?\b/y, type: 'tok-num' },
    { regex: /\b(def|class|return|if|elif|else|for|while|in|try|except|finally|raise|import|from|as|pass|break|continue|lambda|with|yield|True|False|None)\b/y, type: 'tok-kw' }
  ];
}

function isJsonKey(code, nextIndex) {
  let i = nextIndex;
  while (i < code.length && /\s/.test(code[i])) i += 1;
  return code[i] === ':';
}

function wrap(token, type) {
  const cls = type ? ` class="${type}"` : '';
  return `<span${cls}>${escapeHtml(token)}</span>`;
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeChar(char) {
  if (char === '&') return '&amp;';
  if (char === '<') return '&lt;';
  if (char === '>') return '&gt;';
  if (char === '"') return '&quot;';
  return char;
}
